/* eslint-disable react/no-unescaped-entities */
import { IAccount, IOperationResultNoValue, UserRole } from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import { parse as getQueryStringParams } from '@AppModels/QueryStringUtils';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { EventLogger, IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import { getDefaultDateStringValue } from '../../../../models/UiUtils';
import { useManagedAccountsQuery } from '../../../../queries/ManagedAccount';
import { useHasNonProfitIntegration } from '../../../../models/hooks/useHasNonProfitIntegration';
import { Button } from '../../../components-v2/Button';
import { Input } from '../../../components-v2/Input';
import { THEME } from '../../../components-v2/Theme/theme';
import { ISettingsChildComponentProps, withSettingsContext } from '../../../containers/Settings/context';
import { isAccountSuperAdminUser } from '../../../utils/managedAccountAccessSession';
import { brandSecondary, mention } from '../../../styles/colors';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../ConfirmationDialog';
import { DeactivateAccount } from '../../DeactivateAccount';
import { Modal } from '../../Modal';
import { RadioButton } from '../../RadioButton';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../Tooltip';
import { s as tooltipStyles } from '../../Tooltip/styles';
import { AICustomContentGraphic } from '../../svgs/graphics/AICustomContentGraphic';
import { BillingIcon } from '../../svgs/graphics/BillingIcon';
import { BrandSettingsIcon } from '../../svgs/graphics/BrandSettingsIcon';
import { BusinessPlanPlaceholderIcon } from '../../svgs/graphics/BusinessPlanPlaceholderIcon';
import { CalendarInCircleGraphic } from '../../svgs/graphics/CalendarInCircleGraphic';
import { EmailInCircleGraphic } from '../../svgs/graphics/EmailInCircleGraphic';
import { BlogIcon } from '../../svgs/icons/BlogIcon';
import { InfoIcon } from '../../svgs/icons/InfoIcon';
import { NotesPlaceholderIcon } from '../../svgs/icons/NotesPlaceholderIcon';
import { ShareIcon } from '../../svgs/icons/ShareIcon';
import { SvgIcon } from '../../svgs/icons/SvgIcon';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { SettingsGroup } from '../SettingsGroup';
import { AdminSettingsSection } from '../models';
import { styleSheet } from '../styles';
import '../styles.less';
import { AICustomContentSection } from './AICustomContentSection';
import { BrandSettingsSection } from './BrandSettingsSection';
import { CompanySettingsSection } from './CompanySettingsSection';
import { GoogleReviewBrandingSection } from './GoogleReviewBrandingSection';
import { SocialMediaDisclaimerSection } from './SocialMediaDisclaimerSection';
import { BlogSection, HandwrittenCardsSection } from './presentation';

const confirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		title: 'Yes, remove API keys',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

interface IProps
	extends RouteComponentProps<any>,
		IErrorMessageComponentProps,
		IUserSessionComponentProps,
		IEventLoggingComponentProps,
		ISettingsChildComponentProps {
	className?: string;
	sectionToExpand?: AdminSettingsSection;
}

interface IState {
	accountForUpdate?: Partial<IAccount>;
	consumedUsers?: number;
	expandedSection?: AdminSettingsSection | null;
	showDeactivateModal?: boolean;
	selfServicePaymentUrl?: string;
	publishableKey?: string;
	secretKey?: string;
	hasSecretKey?: boolean;
	removeApiKeysModalOpen?: boolean;
	stripeKeyValidationError?: string;
	creditCardProcessingFeePercent?: string;
	stripeTransactionProcessingFee?: string;
	customerPortalUrl?: string;
}

function ManagedAccountsSettingsEntry({
	enabled,
	onManageManagedAccountsClick,
}: {
	enabled: boolean;
	onManageManagedAccountsClick(): void;
}) {
	const managedAccountsQuery = useManagedAccountsQuery({
		enabled,
		meta: {
			toastErrorMessage: 'Could not load managed accounts.',
		},
	});

	if (!enabled || (managedAccountsQuery.data?.length ?? 0) === 0) {
		return null;
	}

	return (
		<div className={css(styleSheet.subsection)}>
			<h5 className={css(styleSheet.subsectionTitle)}>Managed Accounts</h5>
			<div className={css(styleSheet.subsectionDescription)}>
				View the accounts this account manages and switch into a managed account session.
			</div>
			<div className='settings-admin-users-manage'>
				<span className='settings-manageUsersLink' onClick={onManageManagedAccountsClick}>
					Manage accounts
				</span>
			</div>
		</div>
	);
}

function WithoutNonProfitIntegration({ children }: { children: React.ReactNode }) {
	const { hasNonProfitIntegration, isPending } = useHasNonProfitIntegration();
	return isPending || hasNonProfitIntegration ? null : <>{children}</>;
}

class _AdminSettings extends React.Component<IProps, IState> {
	private sectionRefs = {
		[AdminSettingsSection.Blog]: React.createRef<HTMLDivElement>(),
		[AdminSettingsSection.Cards]: React.createRef<HTMLDivElement>(),
		[AdminSettingsSection.Company]: undefined as React.RefObject<HTMLElement>,
		[AdminSettingsSection.BrandSettings]: React.createRef<HTMLDivElement>(),
		[AdminSettingsSection.GoogleReviewBrandingPreferences]: React.createRef<HTMLDivElement>(),
		[AdminSettingsSection.AICustomContent]: React.createRef<HTMLDivElement>(),
		[AdminSettingsSection.Defaults]: undefined as React.RefObject<HTMLElement>,
		[AdminSettingsSection.Meetings]: undefined as React.RefObject<HTMLElement>,
		[AdminSettingsSection.UsersAndBilling]: undefined as React.RefObject<HTMLElement>,
		[AdminSettingsSection.UserGroups]: undefined as React.RefObject<HTMLElement>,
		[AdminSettingsSection.ReceivePayments]: undefined as React.RefObject<HTMLElement>,
		[AdminSettingsSection.SocialMedia]: React.createRef<HTMLDivElement>(),
	};

	constructor(props: IProps) {
		super(props);
		this.state = {
			accountForUpdate: null,
			consumedUsers: -1,
			showDeactivateModal: false,
			expandedSection: props.sectionToExpand,
			selfServicePaymentUrl: undefined,
			publishableKey: '',
			secretKey: '',
			hasSecretKey: false,
			removeApiKeysModalOpen: false,
			creditCardProcessingFeePercent: '',
			stripeTransactionProcessingFee: '',
			customerPortalUrl: '',
		};
	}

	public componentDidMount() {
		const { userSession, history, location } = this.props;
		const { expandedSection } = this.state;

		const accountPromise = userSession.account.load(true);
		if (accountPromise) {
			accountPromise
				.then(() => {
					this.setAccountState(this.props.userSession.account);

					// Load Stripe API keys
					userSession.account
						.getAccountPublicKey()
						.then(keys => {
							if (keys) {
								this.setState({
									publishableKey: keys.value,
									hasSecretKey: !!keys.value,
								});
							}
						})
						.catch(() => {
							this.setState({
								publishableKey: '',
								hasSecretKey: false,
							});
						});

					// Load processing fees from stripe payment features
					const stripeFeatures = this.props.userSession.account.features?.stripePayment;
					if (stripeFeatures) {
						this.setState({
							creditCardProcessingFeePercent:
								Number((stripeFeatures.cardFeePercentage * 100).toFixed(2)).toString() || '',
							stripeTransactionProcessingFee: stripeFeatures.cardFeeFixed?.toFixed(2) || '',
							customerPortalUrl: stripeFeatures.customerPortalUrl || '',
						});
					}
				})
				.catch(() => this.displayErrorMessage('Could not load the account information'));
		}

		const userCountPromise = userSession.account.getUserCount();
		if (userCountPromise) {
			userCountPromise
				.then(count => this.setState({ consumedUsers: count }))
				.catch(() => this.displayErrorMessage('Could not get the number of users on this account'));
		}

		const accountId = userSession.account.id;
		if (accountId) {
			userSession.account
				.getSelfServicePaymentUrl(accountId)
				.then(result => {
					if (result.success) {
						this.setState({ selfServicePaymentUrl: result.value });
					}
				})
				.catch(error => {
					console.error('Could not load the payment link', error);
				});
		}

		// look for command to auto-show deactivate modal
		const queryParams = getQueryStringParams(location.search) || {};
		const showDeactivateModal = queryParams.cmd === 'deactivate' ? true : false;

		this.setState(
			{
				showDeactivateModal,
			},
			() => {
				if (showDeactivateModal) {
					// remove query string
					history.replace(location.pathname);
				}
			}
		);

		if (expandedSection) {
			setTimeout(() => {
				this.sectionRefs[expandedSection]?.current?.scrollIntoView({ behavior: 'smooth' });
			}, 500);
		}
	}

	public render() {
		const { accountForUpdate } = this.state;
		const { settingsContext, userSession } = this.props;
		const planId =
			!!this.props.userSession && !!this.props.userSession.account && !!this.props.userSession.account.planDetails
				? this.props.userSession.account.planDetails.planId
				: 0;
		const defaultUserRole =
			!!accountForUpdate && !!accountForUpdate.preferences && !!accountForUpdate.preferences.defaults.userRole
				? accountForUpdate.preferences.defaults.userRole
				: 'user';
		const defaultVisibility =
			!!accountForUpdate && !!accountForUpdate.preferences && !!accountForUpdate.preferences.defaults.visibility
				? accountForUpdate.preferences.defaults.visibility
				: 'all';
		const ignoreInternalMeetings =
			!!accountForUpdate && !!accountForUpdate.preferences ? accountForUpdate.preferences.ignoreInternalMeetings : true;
		const validationError = this.state.stripeKeyValidationError;
		const nextBill = this.props.userSession.account.planDetails?.nextBill;
		const canViewManagedAccounts = isAccountSuperAdminUser(userSession.user);
		const handleCloseSettingGroup = () => this.setState({ expandedSection: null });

		return (
			<div className='settings-admin-container'>
				<div className={css(styleSheet.root)}>
					{userSession.account.features.blogFeature?.enabled &&
					(!settingsContext?.supportedAdminSettingsSections ||
						settingsContext.supportedAdminSettingsSections.includes(AdminSettingsSection.Blog)) ? (
						<SettingsGroup
							name='Blog Settings'
							description='Manage blog information'
							icon={
								<SvgIcon height={60} width={60}>
									<g fill='none' fillRule='evenodd'>
										<circle cx='30' cy='30' r='30' fill={mention} />
										<g transform='translate(18 15)'>
											<BlogIcon fill={brandSecondary} width={26} height={26} />
										</g>
									</g>
								</SvgIcon>
							}
							expanded={this.state.expandedSection === AdminSettingsSection.Blog}
							onToggleExpansion={expanded =>
								this.setState({ expandedSection: expanded ? AdminSettingsSection.Blog : null })
							}
						>
							<div ref={this.sectionRefs[AdminSettingsSection.Blog]}>
								<BlogSection />
							</div>
						</SettingsGroup>
					) : null}
					{userSession.account.features.handwrittenCards?.enabled &&
					(!settingsContext?.supportedAdminSettingsSections ||
						settingsContext.supportedAdminSettingsSections.includes(AdminSettingsSection.Cards)) ? (
						<SettingsGroup
							name='Card Settings'
							description='Configure defaults settings for cards.'
							icon={<EmailInCircleGraphic width={60} height={60} strokeColor={brandSecondary} bgCircleFill={mention} />}
							expanded={this.state.expandedSection === AdminSettingsSection.Cards}
							onToggleExpansion={expanded =>
								this.setState({ expandedSection: expanded ? AdminSettingsSection.Cards : null })
							}
						>
							<div ref={this.sectionRefs[AdminSettingsSection.Cards]}>
								<HandwrittenCardsSection onCancel={handleCloseSettingGroup} />
							</div>
						</SettingsGroup>
					) : null}
					{!settingsContext?.supportedAdminSettingsSections ||
					settingsContext.supportedAdminSettingsSections.includes(AdminSettingsSection.Company) ? (
						<SettingsGroup
							name='Company Settings'
							description='Manage company information'
							icon={
								<BusinessPlanPlaceholderIcon
									strokeColor={brandSecondary}
									bgCircleFill={mention}
									width={60}
									height={60}
								/>
							}
							expanded={this.state.expandedSection === AdminSettingsSection.Company}
							onToggleExpansion={expanded =>
								this.setState({ expandedSection: expanded ? AdminSettingsSection.Company : null })
							}
						>
							<CompanySettingsSection
								accountForUpdate={this.state.accountForUpdate}
								onUpdateFormFieldValue={this.updateFormFieldValue}
								onSaveClick={this.saveClick}
								onCancelClick={() => this.handleCancelCompanySettings()}
								getField={this.getField}
							/>
						</SettingsGroup>
					) : null}
					{userSession.account.features?.contentGeneration?.enabled &&
					(!settingsContext?.supportedAdminSettingsSections ||
						settingsContext.supportedAdminSettingsSections.includes(AdminSettingsSection.BrandSettings)) ? (
						<SettingsGroup
							name='Brand Settings'
							description='Configure brand colors, fonts, and images for AI-generated content'
							icon={<BrandSettingsIcon width={60} height={60} />}
							expanded={this.state.expandedSection === AdminSettingsSection.BrandSettings}
							onToggleExpansion={expanded =>
								this.setState({ expandedSection: expanded ? AdminSettingsSection.BrandSettings : null })
							}
						>
							<div ref={this.sectionRefs[AdminSettingsSection.BrandSettings]}>
								<BrandSettingsSection />
							</div>
						</SettingsGroup>
					) : null}

					{userSession.account.features?.contentGeneration?.enabled &&
					(!settingsContext?.supportedAdminSettingsSections ||
						settingsContext.supportedAdminSettingsSections.includes(
							AdminSettingsSection.GoogleReviewBrandingPreferences
						)) ? (
						<SettingsGroup
							name='Google Review Branding Preferences'
							description='Configure default colors, toggles, and background image for Google Review testimonial posts.'
							icon={<BrandSettingsIcon width={60} height={60} />}
							expanded={this.state.expandedSection === AdminSettingsSection.GoogleReviewBrandingPreferences}
							onToggleExpansion={expanded =>
								this.setState({
									expandedSection: expanded ? AdminSettingsSection.GoogleReviewBrandingPreferences : null,
								})
							}
						>
							<div ref={this.sectionRefs[AdminSettingsSection.GoogleReviewBrandingPreferences]}>
								<GoogleReviewBrandingSection />
							</div>
						</SettingsGroup>
					) : null}

					{userSession.account.features?.contentGeneration?.enabled &&
					(!settingsContext?.supportedAdminSettingsSections ||
						settingsContext.supportedAdminSettingsSections.includes(AdminSettingsSection.AICustomContent)) ? (
						<SettingsGroup
							name='AI Custom Content Preferences'
							description='Configure the AI assistant with your preferences to use when generating customized content for your account.'
							icon={
								<AICustomContentGraphic strokeColor={brandSecondary} bgCircleFill={mention} width={60} height={60} />
							}
							expanded={this.state.expandedSection === AdminSettingsSection.AICustomContent}
							onToggleExpansion={expanded =>
								this.setState({ expandedSection: expanded ? AdminSettingsSection.AICustomContent : null })
							}
						>
							<div ref={this.sectionRefs[AdminSettingsSection.AICustomContent]}>
								<AICustomContentSection />
							</div>
						</SettingsGroup>
					) : null}
					{!settingsContext?.supportedAdminSettingsSections ||
					settingsContext.supportedAdminSettingsSections.includes(AdminSettingsSection.SocialMedia) ? (
						<SettingsGroup
							name='Social Media Settings'
							description='Configure a default disclaimer for social media posts.'
							icon={
								<SvgIcon height={60} width={60}>
									<g fill='none' fillRule='evenodd'>
										<circle cx='30' cy='30' r='30' fill={mention} />
										<g transform='translate(18 18)'>
											<ShareIcon fillColor={brandSecondary} width={24} height={24} />
										</g>
									</g>
								</SvgIcon>
							}
							expanded={this.state.expandedSection === AdminSettingsSection.SocialMedia}
							onToggleExpansion={expanded =>
								this.setState({ expandedSection: expanded ? AdminSettingsSection.SocialMedia : null })
							}
						>
							<div ref={this.sectionRefs[AdminSettingsSection.SocialMedia]}>
								<SocialMediaDisclaimerSection />
							</div>
						</SettingsGroup>
					) : null}
					{!settingsContext?.supportedAdminSettingsSections ||
					settingsContext.supportedAdminSettingsSections.includes(AdminSettingsSection.Defaults) ? (
						<SettingsGroup
							name='Default Settings'
							description='Set and manage default settings.'
							icon={<NotesPlaceholderIcon width={60} height={60} strokeColor={brandSecondary} bgCircleFill={mention} />}
							expanded={this.state.expandedSection === AdminSettingsSection.Defaults}
							onToggleExpansion={expanded =>
								this.setState({ expandedSection: expanded ? AdminSettingsSection.Defaults : null })
							}
						>
							<div className='settings-admin-company-container'>
								<div className='settings-sectionHeader'>Defaults</div>
								<div className={css(baseStyleSheet.mb2)}>
									<span className='settings-sectionSubHeaderGray'>User Permission:</span>
									<RadioButton
										checked={defaultUserRole === 'user'}
										className='settings-radioButton'
										id='user'
										name='defaultUserRoleRadioButtonGroup'
										onChange={this.defaultUserRoleRadioButtionChanged('user')}
									>
										Regular
									</RadioButton>
									<RadioButton
										checked={defaultUserRole === 'limitedUser'}
										className='settings-radioButton'
										id='limitedUser'
										name='defaultUserRoleRadioButtonGroup'
										onChange={this.defaultUserRoleRadioButtionChanged('limitedUser')}
									>
										Limited (limited users would only be able to see his/her own contacts)
									</RadioButton>
								</div>
								<div className={css(baseStyleSheet.mb2)}>
									<span className='settings-sectionSubHeaderGray'>
										Visibility for new contacts, notes and action items
									</span>
									<RadioButton
										checked={defaultVisibility === 'all'}
										className='settings-radioButton'
										id='allVisibility'
										name='defaulVisibilityRadioButtionChanged'
										onChange={this.defaulVisibilityRadioButtionChanged('all')}
									>
										Shared with coworkers
									</RadioButton>
									<RadioButton
										checked={defaultVisibility !== 'all'}
										className='settings-radioButton'
										id='adminVisibility'
										name='defaulVisibilityRadioButtionChanged'
										onChange={this.defaulVisibilityRadioButtionChanged('admin')}
									>
										Private
									</RadioButton>
								</div>
								<div className={css(baseStyleSheet.mb2, baseStyleSheet.mt4, styleSheet.buttonContainer)}>
									<Button onClick={this.saveClick}>Save</Button>
									<Button variant='secondary' onClick={this.handleDefaultSettingsCancel}>
										Cancel
									</Button>
								</div>
							</div>
						</SettingsGroup>
					) : null}
					{!settingsContext?.supportedAdminSettingsSections ||
					settingsContext.supportedAdminSettingsSections.includes(AdminSettingsSection.Meetings) ? (
						<SettingsGroup
							name='Meeting Settings'
							description='Adjust meeting preferences and settings.'
							icon={
								<CalendarInCircleGraphic width={60} height={60} strokeColor={brandSecondary} bgCircleFill={mention} />
							}
							expanded={this.state.expandedSection === AdminSettingsSection.Meetings}
							onToggleExpansion={expanded =>
								this.setState({ expandedSection: expanded ? AdminSettingsSection.Meetings : null })
							}
						>
							<div className='settings-admin-meetings-container'>
								<div className='settings-sectionSubHeaderGray'>
									Do you want reminders to record notes for internal meetings?
								</div>
								<div className='settings-radioButtonContainer'>
									<RadioButton
										checked={ignoreInternalMeetings}
										className='settings-radioButton'
										id='ignoreInternalMeetings'
										name='internalMeetingsRadioButtonGroup'
										onChange={this.internalMeetingRadioButtonChanged(true)}
									>
										Hide internal meetings
									</RadioButton>
									<RadioButton
										checked={!ignoreInternalMeetings}
										className='settings-radioButton'
										id='showInternalMeetings'
										name='internalMeetingsRadioButtonGroup'
										onChange={this.internalMeetingRadioButtonChanged(false)}
									>
										Show internal meetings
									</RadioButton>
								</div>
								<div className={css(baseStyleSheet.mb2, baseStyleSheet.mt4, styleSheet.buttonContainer)}>
									<Button onClick={this.saveClick}>Save</Button>
									<Button variant='secondary' onClick={this.handleMeetingsSettingsCancel}>
										Cancel
									</Button>
								</div>
							</div>
						</SettingsGroup>
					) : null}
					{this.props.userSession?.account?.stripePayment?.enabled &&
					(!settingsContext?.supportedAdminSettingsSections ||
						settingsContext.supportedAdminSettingsSections.includes(AdminSettingsSection.ReceivePayments)) ? (
						<SettingsGroup
							name='Receive Payments'
							description='Manage receiving payments'
							icon={
								<SvgIcon height={60} width={60}>
									<g fill='none' fillRule='evenodd'>
										<circle cx='30' cy='30' r='30' fill={mention} />
										<BillingIcon width={60} height={60} />
									</g>
								</SvgIcon>
							}
							expanded={this.state.expandedSection === AdminSettingsSection.ReceivePayments}
							onToggleExpansion={expanded =>
								this.setState({ expandedSection: expanded ? AdminSettingsSection.ReceivePayments : null })
							}
						>
							<section
								className={`settings-admin-company-container ${css(bs.flex, bs.flexCol, styleSheet.paymentSettings)}`}
							>
								<article className={css(bs.flex, bs.flexCol, styleSheet.stripeKeysSection)}>
									<h4 className={css(styleSheet.sectionTitle)}>Stripe API Keys</h4>
									<p className={css(styleSheet.sectionLead)}>
										Paste your Stripe Keys to start accepting payments for events.{' '}
										<a
											className={css(bs.link, bs.textBrandPrimary)}
											href='https://help.levitate.ai/article/475-paid-events'
											target='_blank'
											rel='noopener noreferrer'
										>
											Learn more
										</a>
									</p>
									{validationError && (
										<div className={css(baseStyleSheet.mb2, styleSheet.validation)}>
											{validationError === 'invalid_publishable_key' && (
												<>
													Invalid publishable key format. Stripe publishable keys start with 'pk_'. Find your keys at{' '}
													<a href='https://dashboard.stripe.com/apikeys' target='_blank' rel='noopener noreferrer'>
														https://dashboard.stripe.com/apikeys
													</a>
												</>
											)}
											{validationError === 'publishable_key_in_secret_field' && (
												<>
													You entered a publishable key (starts with 'pk_'). Please enter your Stripe secret key (starts
													with 'sk_') instead. Find your keys at{' '}
													<a href='https://dashboard.stripe.com/apikeys' target='_blank' rel='noopener noreferrer'>
														https://dashboard.stripe.com/apikeys
													</a>
												</>
											)}
											{validationError === 'invalid_secret_key' && (
												<>
													Invalid key format. Stripe secret keys start with 'sk_'. Find your keys at{' '}
													<a href='https://dashboard.stripe.com/apikeys' target='_blank' rel='noopener noreferrer'>
														https://dashboard.stripe.com/apikeys
													</a>
												</>
											)}
											{validationError === 'Please enter both keys' && validationError}
										</div>
									)}
									<div className={css(baseStyleSheet.mb2)}>
										<label className={css(styleSheet.formFieldLabel)} htmlFor='editPublishableKey'>
											Publishable Key
										</label>
										<Input
											type='text'
											id='editPublishableKey'
											placeholder='Publishable Key (required)'
											styles={[baseStyleSheet.textFieldStandard]}
											value={this.state.accountForUpdate ? this.getField(this.state.publishableKey) : ''}
											onChange={this.updateFormFieldValue}
											disabled={this.state.hasSecretKey}
										/>
									</div>
									<div className={css(baseStyleSheet.mb2)}>
										<label className={css(styleSheet.formFieldLabel)} htmlFor='editSecretKey'>
											Secret Key
										</label>
										<Input
											type='password'
											id='editSecretKey'
											placeholder={this.state.hasSecretKey ? '*********SECRET KEY*********' : 'Secret Key (required)'}
											styles={[baseStyleSheet.textFieldStandard]}
											value={this.state.accountForUpdate ? this.getField(this.state.secretKey) : ''}
											onChange={this.updateFormFieldValue}
											disabled={this.state.hasSecretKey}
										/>
									</div>
									<footer className={css(bs.flex, bs.gap2, bs.mt2)}>
										{this.state.hasSecretKey ? (
											<>
												<Button
													variant='destructive-secondary'
													styles={[styleSheet.deleteKeysButton]}
													type='button'
													onClick={this.removeStripeKeys}
												>
													Delete Keys
												</Button>
												<ConfirmationDialog
													icon={<WarningIcon />}
													title='Delete Stripe API Keys?'
													options={confirmationOptions}
													modalProps={{
														isOpen: this.state.removeApiKeysModalOpen,
														onRequestClose: this.handleRequestClose,
													}}
												>
													Are you sure you want to Delete the Stripe Api Keys?
												</ConfirmationDialog>
											</>
										) : (
											<Button
												type='button'
												onClick={this.saveStripeClick}
												disabled={
													!this.state.accountForUpdate ||
													!!this.state.stripeKeyValidationError ||
													!this.state.publishableKey ||
													!this.state.secretKey
												}
											>
												Save Keys
											</Button>
										)}
										<Button variant='secondary' type='button' onClick={this.cancelStripeKeysClick}>
											Cancel
										</Button>
									</footer>
								</article>
								{userSession?.account?.features?.boards?.donationBoard?.enabled ? (
									<WithoutNonProfitIntegration>
										<article className={css(bs.flex, bs.flexCol, styleSheet.processingFeesSection)}>
											<h4 className={css(styleSheet.sectionTitle)}>
												Processing Fees{' '}
												<Tooltip>
													<TooltipTrigger asChild>
														<span>
															<InfoIcon fillColor={THEME.colors.icon.primary} className={css(baseStyleSheet.ml1)} />
														</span>
													</TooltipTrigger>
													<TooltipContent>
														These Stripe fee settings apply only to donation collection transactions
													</TooltipContent>
												</Tooltip>
											</h4>
											<p className={css(styleSheet.sectionLead)}>Configure your payment processing fee settings.</p>
											<div className={css(baseStyleSheet.mb2)}>
												<label className={css(styleSheet.formFieldLabel)} htmlFor='editCreditCardProcessingFeePercent'>
													Card Processing %
												</label>
												<Input
													type='text'
													id='editCreditCardProcessingFeePercent'
													placeholder='0.00'
													styles={[baseStyleSheet.textFieldStandard]}
													value={this.state.creditCardProcessingFeePercent}
													onChange={this.updateFormFieldValue}
												/>
											</div>
											<div className={css(baseStyleSheet.mb2)}>
												<label className={css(styleSheet.formFieldLabel)} htmlFor='editStripeTransactionProcessingFee'>
													Per Transaction Fee $
												</label>
												<Input
													type='text'
													id='editStripeTransactionProcessingFee'
													placeholder='0.00'
													styles={[baseStyleSheet.textFieldStandard]}
													value={this.state.stripeTransactionProcessingFee}
													onChange={this.updateFormFieldValue}
												/>
											</div>
										</article>
										<article>
											<h4 className={css(styleSheet.sectionTitle)}>Customer Portal Link</h4>
											<p className={css(styleSheet.sectionLead)}>Configure your Stripe customer portal link.</p>
											<div className={css(baseStyleSheet.mb2)}>
												<label
													className={css(styleSheet.formFieldLabel, bs.flex, bs.itemsCenter, bs.gap1)}
													htmlFor='editPaymentProcessingUrl'
												>
													Portal Link
													<Tooltip>
														<TooltipTrigger>
															<InfoIcon fillColor={THEME.colors.primary.main} />
														</TooltipTrigger>
														<TooltipContent styles={[tooltipStyles.contentWithMaxWidth]}>
															This link is required for recurring monthly and annual donations. Donors will use this
															portal to manage their subscriptions.
														</TooltipContent>
													</Tooltip>
												</label>
												<Input
													type='text'
													id='editCustomerPortalUrl'
													styles={[baseStyleSheet.textFieldStandard]}
													value={this.state.customerPortalUrl}
													onChange={this.updateFormFieldValue}
												/>
											</div>
										</article>
										<footer className={css(bs.flex, bs.gap2, bs.mt2)}>
											<>
												<Button type='button' onClick={this.saveProcessingFeesClick}>
													Save Settings
												</Button>
											</>
											<Button variant='secondary' type='button' onClick={this.cancelProcessingFeesClick}>
												Cancel
											</Button>
										</footer>
									</WithoutNonProfitIntegration>
								) : null}
							</section>
						</SettingsGroup>
					) : null}
					{!settingsContext?.supportedAdminSettingsSections ||
					settingsContext.supportedAdminSettingsSections.includes(AdminSettingsSection.UsersAndBilling) ? (
						<SettingsGroup
							name='Users & Billing'
							description='Manage user accounts, billing information, and subscription plans.'
							icon={
								<SvgIcon height={60} width={60}>
									<g fill='none' fillRule='evenodd'>
										<circle cx='30' cy='30' r='30' fill={mention} />
										<BillingIcon width={60} height={60} />
									</g>
								</SvgIcon>
							}
							expanded={this.state.expandedSection === AdminSettingsSection.UsersAndBilling}
							onToggleExpansion={expanded =>
								this.setState({ expandedSection: expanded ? AdminSettingsSection.UsersAndBilling : null })
							}
						>
							<div className={planId > 0 ? css(styleSheet.usersSection) : 'hide'}>
								<div className={css(styleSheet.subsection)}>
									<h5 className={css(styleSheet.subsectionTitle)}>Users</h5>
									<div className={css(styleSheet.subsectionDescription)}>
										{this.getLicenseInfo()} Please{' '}
										<a className='settings-contactSupportLink' href='mailto:support@levitateapp.com'>
											contact support
										</a>{' '}
										if you would like to add or remove users.
									</div>
									<div className='settings-admin-users-manage'>
										<span className='settings-manageUsersLink' onClick={this.manageUsersClick}>
											Manage users
										</span>
									</div>
								</div>
								<div className={css(styleSheet.subsection)}>
									<h5 className={css(styleSheet.subsectionTitle)}>User Groups</h5>
									<div className={css(styleSheet.subsectionDescription)}>
										User groups allow you to organize users and manage their permissions for different features like
										surveys.
									</div>
									<div className='settings-admin-users-manage'>
										<span className='settings-manageUsersLink' onClick={this.manageUserGroupsClick}>
											Manage user groups
										</span>
									</div>
								</div>
								<ManagedAccountsSettingsEntry
									enabled={canViewManagedAccounts}
									onManageManagedAccountsClick={this.manageManagedAccountsClick}
								/>
								{nextBill ? (
									<div className={css(styleSheet.subsection)}>
										<h5 className={css(styleSheet.subsectionTitle)}>Billing</h5>
										<div>
											<span className={css(styleSheet.formFieldLabel)}>Next Bill Due Date</span>
											<span className={css(styleSheet.formFieldValue)}>{getDefaultDateStringValue(nextBill.date)}</span>
										</div>
										<div>
											<span className={css(styleSheet.formFieldLabel)}>Next Bill Amount</span>
											<span className={css(styleSheet.formFieldValue)}>${nextBill.amount ?? 3500}</span>
										</div>
										{this.state.selfServicePaymentUrl ? (
											<a
												className='settings-updateCreditCardLink'
												href={this.state.selfServicePaymentUrl}
												target='_blank'
												rel='noopener noreferrer'
											>
												Update Credit Card
											</a>
										) : null}
									</div>
								) : null}
							</div>
						</SettingsGroup>
					) : null}
					<div className='settings-sectionHeader'>
						<span className='settings-sectionSubHeaderGray'>
							If you no longer wish to use Levitate, you may{' '}
							<Button variant='link' onClick={this.toggleDeactivateModal(true)}>
								deactivate your account
							</Button>
							.
						</span>
					</div>
				</div>
				<Modal
					isOpen={this.state.showDeactivateModal}
					onRequestClose={this.toggleDeactivateModal(false)}
					shouldCloseOnOverlayClick={true}
					useDefaultHeader={true}
				>
					<DeactivateAccount onCancel={this.toggleDeactivateModal(false)} onDeactivated={this.onAccountDeactivated} />
				</Modal>
			</div>
		);
	}

	private displayErrorMessage = (errorMsg: string) => {
		if (this.props.errorMessages) {
			this.props.errorMessages.push({
				messages: [errorMsg],
			});
		}
	};

	private saveClick = () => {
		const updatedAccount = {
			...this.props.userSession.account.toJs(),
			...this.state.accountForUpdate,
		};

		const promise = this.props.userSession.account.save(updatedAccount);
		if (promise) {
			promise.then(this.saveSuccess).catch(this.saveError);
		}
	};

	private saveStripeClick = async () => {
		const stripePayload = {
			publishableKey: this.state.publishableKey,
			secretKey: this.state.secretKey,
		};

		// Validation should already be done in onChange, but double-check here
		if (!stripePayload.publishableKey || !stripePayload.secretKey || this.state.stripeKeyValidationError) {
			return;
		}

		try {
			EventLogger.logInput('AdminSettings', 'Save-Stripe-Keys', 'Click');
			await this.props.userSession.account.saveStripeKeys(stripePayload);
			this.showSuccessToaster();
			this.setState({ hasSecretKey: true, secretKey: '', stripeKeyValidationError: undefined });
		} catch (error) {
			this.saveError(error);
			this.setState({ hasSecretKey: false, removeApiKeysModalOpen: false });
		}
	};

	private removeStripeKeys = () => {
		this.setState({ removeApiKeysModalOpen: true });
	};

	private handleRequestClose = async (result: boolean, canceled?: boolean) => {
		if (canceled) {
			this.setState({ removeApiKeysModalOpen: false });
			return;
		}
		if (result) {
			try {
				EventLogger.logInput('AdminSettings', 'Delete-Stripe-Keys', 'Click');
				await this.props.userSession.account.removeStripeKeys();
				this.showSuccessToaster();
				this.setState({ removeApiKeysModalOpen: false, hasSecretKey: false, secretKey: '', publishableKey: '' });
			} catch (error) {
				this.saveError(error);
			}
		}
	};

	private setAccountState = (account: IAccount) => {
		// Explicitly operate on a subset of properties from account.
		// We don't want intermediate updates to propagate outside of this context.
		const accountPartial = {
			companyName: account.companyName,
			preferences: { ...account.preferences },
		};

		if (!accountPartial.preferences.defaults) {
			accountPartial.preferences.defaults = {};
		}

		accountPartial.preferences.defaults = {
			...accountPartial.preferences.defaults,
		};

		this.setState({ accountForUpdate: accountPartial });
	};

	private showSuccessToaster = () => {
		toast.success('Changes were saved successfully!');
	};

	private saveSuccess = (account: IAccount) => {
		this.setAccountState(account);
		this.showSuccessToaster();
	};

	private saveError = (error: IOperationResultNoValue) => {
		this.displayErrorMessage(error.systemMessage);
	};

	private cancelStripeKeysClick = () => {
		// Clear validation errors and Stripe form fields
		this.setState({
			stripeKeyValidationError: undefined,
			publishableKey: undefined,
			secretKey: undefined,
		});
	};

	private cancelProcessingFeesClick = () => {
		// Clear processing fees form fields
		this.setState({
			creditCardProcessingFeePercent: '',
			stripeTransactionProcessingFee: '',
		});
	};

	private saveProcessingFeesClick = async () => {
		try {
			EventLogger.logInput('AdminSettings', 'Save-Processing-Fees', 'Click');

			const creditCardFeePercent =
				Number((parseFloat(this.state.creditCardProcessingFeePercent) / 100).toFixed(3)) || 0;
			const transactionFee = parseFloat(this.state.stripeTransactionProcessingFee) || 0;

			// Validate the input
			if (creditCardFeePercent < 0 || creditCardFeePercent > 100) {
				throw new Error('Credit Card Processing Fee % must be between 0 and 100');
			}

			if (transactionFee < 0) {
				throw new Error('Stripe Transaction Processing Fee must be a positive number');
			}
			if (!this.state.customerPortalUrl) {
				throw new Error('Payment Processing URL is required');
			}
			// Validate URL format
			try {
				new URL(this.state.customerPortalUrl);
			} catch {
				throw new Error('Payment Processing URL must be a valid URL');
			}
			// Prepare fees data for the specific fees endpoint
			const feesData = {
				cardFeePercentage: creditCardFeePercent,
				cardFeeFixed: transactionFee,
				customerPortalUrl: this.state.customerPortalUrl,
			};

			// Save using the account viewmodel
			await this.props.userSession.account.saveStripePaymentFees(feesData);

			this.showSuccessToaster();
		} catch (error) {
			if (error instanceof Error) {
				this.displayErrorMessage(error.message);
			} else {
				this.saveError(error);
			}
		}
	};

	private cancelReceivePaymentsClick = () => {
		// Clear all fields and close section
		this.setState({
			stripeKeyValidationError: undefined,
			publishableKey: undefined,
			secretKey: undefined,
			creditCardProcessingFeePercent: '',
			stripeTransactionProcessingFee: '',
		});
		this.setState({ expandedSection: null });
	};

	private manageUsersClick = () => {
		this.props.history.push(`/settings/admin/manage-users`);
	};

	private manageUserGroupsClick = () => {
		this.props.history.push(`/settings/admin/manage-groups`);
	};

	private manageManagedAccountsClick = () => {
		this.props.history.push('/managed-accounts');
	};

	private handleCancelCompanySettings = () => {
		this.setState({
			accountForUpdate: {
				...this.state.accountForUpdate,
				companyName: this.props.userSession.account.companyName,
			},
		});
		this.setState({ expandedSection: null });
	};

	private updateFormFieldValue = (e: React.ChangeEvent<HTMLInputElement>) => {
		const elementId = e.target.id;
		const account = this.state.accountForUpdate;
		switch (elementId) {
			case 'editCompanyName':
				account.companyName = e.target.value;
				break;
			case 'editPublishableKey': {
				const value = e.target.value;
				this.setState({ publishableKey: value });

				// Real-time validation for publishable key
				if (value && !value.startsWith('pk_')) {
					this.setState({ stripeKeyValidationError: 'invalid_publishable_key' });
				} else if (value || this.state.secretKey) {
					// Clear error if valid format or if empty (will check both keys on secret key change)
					this.validateBothStripeKeys(value, this.state.secretKey);
				} else {
					this.setState({ stripeKeyValidationError: undefined });
				}
				break;
			}
			case 'editSecretKey': {
				const value = e.target.value;
				this.setState({ secretKey: value });

				// Real-time validation for secret key
				if (value) {
					if (value.startsWith('pk_')) {
						this.setState({ stripeKeyValidationError: 'publishable_key_in_secret_field' });
					} else if (!value.startsWith('sk_')) {
						this.setState({ stripeKeyValidationError: 'invalid_secret_key' });
					} else {
						// Valid format, check if both keys are present
						this.validateBothStripeKeys(this.state.publishableKey, value);
					}
				} else if (this.state.publishableKey) {
					// If secret key is empty but publishable key exists
					this.validateBothStripeKeys(this.state.publishableKey, value);
				} else {
					this.setState({ stripeKeyValidationError: undefined });
				}
				break;
			}
			case 'editCreditCardProcessingFeePercent': {
				const value = e.target.value;
				// Allow only numbers and decimal point
				if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
					this.setState({ creditCardProcessingFeePercent: value });
				}
				break;
			}
			case 'editStripeTransactionProcessingFee': {
				const value = e.target.value;
				// Allow only numbers and decimal point
				if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
					this.setState({ stripeTransactionProcessingFee: value });
				}
				break;
			}
			case 'editCustomerPortalUrl': {
				const value = e.target.value;
				this.setState({ customerPortalUrl: value });
				break;
			}
			default:
			// Do nothing
		}
		this.setState({ accountForUpdate: account });
	};

	private validateBothStripeKeys = (publishableKey?: string, secretKey?: string) => {
		// Only show "both keys" error if at least one key has content
		if ((publishableKey || secretKey) && (!publishableKey || !secretKey)) {
			this.setState({ stripeKeyValidationError: 'Please enter both keys' });
		} else if (publishableKey && secretKey) {
			// Both keys present and valid format
			if (!publishableKey.startsWith('pk_')) {
				this.setState({ stripeKeyValidationError: 'invalid_publishable_key' });
			} else if (secretKey.startsWith('pk_')) {
				this.setState({ stripeKeyValidationError: 'publishable_key_in_secret_field' });
			} else if (!secretKey.startsWith('sk_')) {
				this.setState({ stripeKeyValidationError: 'invalid_secret_key' });
			} else {
				this.setState({ stripeKeyValidationError: undefined });
			}
		} else {
			this.setState({ stripeKeyValidationError: undefined });
		}
	};

	private getField(field: string) {
		return field || '';
	}

	private handleMeetingsSettingsCancel = () => {
		this.setState({
			accountForUpdate: {
				...this.state.accountForUpdate,
				preferences: {
					...this.state.accountForUpdate?.preferences,
					ignoreInternalMeetings: this.props.userSession.account.preferences.ignoreInternalMeetings,
				},
			},
		});
		this.setState({ expandedSection: null });
	};

	private internalMeetingRadioButtonChanged = (ignoreInternalMeetings: boolean) => () => {
		const accountForUpdate = this.state.accountForUpdate;

		if (accountForUpdate) {
			accountForUpdate.preferences.ignoreInternalMeetings = ignoreInternalMeetings;
			this.setState({ accountForUpdate });
		}
	};

	private handleDefaultSettingsCancel = () => {
		this.setState({
			accountForUpdate: {
				...this.state.accountForUpdate,
				preferences: {
					...this.state.accountForUpdate?.preferences,
					defaults: {
						...this.state.accountForUpdate?.preferences?.defaults,
						userRole: this.props.userSession.account.preferences.defaults.userRole,
						visibility: this.props.userSession.account.preferences.defaults.visibility,
					},
				},
			},
		});
		this.setState({ expandedSection: null });
	};

	private defaultUserRoleRadioButtionChanged = (role: UserRole) => () => {
		const accountForUpdate = this.state.accountForUpdate;

		if (accountForUpdate) {
			accountForUpdate.preferences.defaults.userRole = role;
			this.setState({ accountForUpdate });
		}
	};

	private defaulVisibilityRadioButtionChanged = (visibility: 'admin' | 'all') => () => {
		const accountForUpdate = this.state.accountForUpdate;

		if (accountForUpdate) {
			accountForUpdate.preferences.defaults.visibility = visibility;
			this.setState({ accountForUpdate });
		}
	};

	private getTotalLicenses = () => {
		const planDetails =
			!!this.props.userSession.account && !!this.props.userSession.account.planDetails
				? this.props.userSession.account.planDetails
				: null;
		return planDetails ? planDetails.users : -1;
	};

	private getConsumedLicenses = () => {
		return this.state.consumedUsers ? this.state.consumedUsers : -1;
	};

	private getLicenseInfo = () => {
		const consumedUsers = this.getConsumedLicenses();
		const totalLicenses = this.getTotalLicenses();

		return consumedUsers > -1 && totalLicenses > -1 ? (
			<>
				You have used <strong>{consumedUsers.toString()}</strong> of {totalLicenses.toString()} licenses.
			</>
		) : (
			<>Loading user license information...</>
		);
	};

	private toggleDeactivateModal = (show: boolean) => () => {
		if (show) {
			this.props.history.replace(`/settings/admin/deactivate-account`);
			EventLogger.logInput('AdminSettings', 'Deactivate', 'Click');
		} else {
			this.props.history.replace(`/settings/admin`);
			EventLogger.logInput('AdminSettings', 'Deactivate-Cancel', 'Click');
		}
		this.setState({
			showDeactivateModal: show,
		});
	};

	private onAccountDeactivated = () => {
		this.props.history.push('/logout');
	};
}

const ObservableAdminSettings = observer(_AdminSettings);
const AdminSettingsWithContext = inject(ErrorMessagesViewModelKey, UserSessionViewModelKey)(ObservableAdminSettings);
const AdminSettingsWithRouter = withRouter(AdminSettingsWithContext);
export const AdminSettings = withSettingsContext(withEventLogging(AdminSettingsWithRouter, 'AdminSettings'));
